import React, { useState, useEffect } from "react";

import FormRegister from "./FormRegister";

import "../../components/form-view/form-view.css";
import image_default from "../../images/sales-overview/no-product-image.jpg";
import Data from "../../data/Data";
import useForm from "../../hooks/useForm";

import { initializeRevision } from "../../utils/revision-utils/RevisionUtils";
import { initializeOrganization } from "../../utils/organization-utils/OrganizationUtils";
import { buildCredential } from "../../utils/credential-utils/CredentialUtils";
import {
    buildCreateMiniApp,
    buildCreateMiniAppPayload
} from "../../utils/create-miniapp-utils/CreateMiniAppUtils";
import FormUtils from "../../utils/form-utils/FormUtils";
import Validate from "../../services/validate-service/Validate";
import MiniAppService from "../../services/mini-app-service/MiniAppService";
import AuthService from "../../services/auth-service/AuthService";
import ErrorHandler from "../../utils/ErrorHandler";
import AlertMessages from "../../utils/AlertMessages";
import Utils from "./Utils";
import ScopesEnum from "../../utils/enums/ScopesEnum";

const MiniAppRegisterScreen = () => {
    const getUserInfo = () => AuthService.getDecodedToken();

    const credentialVO = buildCredential({
        clientId: "",
        clientSecret: "",
        grantType: "client_credentials"
    });
    const revisionVO = initializeRevision(getUserInfo().email);
    const initialOrganization = initializeOrganization(getUserInfo().email);
    const miniAppObj = buildCreateMiniApp(
        Data.getMiniAppPayload(
            credentialVO,
            [revisionVO],
            image_default,
            initialOrganization
        )
    );

    const { values, setValue, setAllValues } = useForm(miniAppObj);
    // eslint-disable-next-line no-unused-vars
    const [collapse, setCollapse] = useState(false);
    const [alertTheme, setAlertTheme] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [visible, setVisible] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [showInputError, setShowInputError] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [, setHasAuthError] = useState(false);
    const [targetValue, setTargetValue] = useState("no_opt");
    const [hasWriteMiniAppScope, setHasWriteMiniAppScope] = useState(false);

    useEffect(() => {
        if (AuthService.isAdmin()) setIsAdmin(true);
    }, []);

    useEffect(() => {
        async function getScopes() {
            const decodedToken = await AuthService.getDecodedToken();
            setHasWriteMiniAppScope(
                decodedToken.scopes.includes(ScopesEnum.WRITE_MINAPP)
            );
        }
        getScopes();
    }, []);

    const updateValues = miniApp => {
        setAllValues(miniApp);
    };

    const onTogglePermission = str => {
        if (isAdmin)
            updateValues(FormUtils.getMiniAppWithNewPermissions(str, values));
    };

    const registerMiniapp = async event => {
        event.preventDefault();
        let miniAppDeepClone = JSON.parse(JSON.stringify(values));

        miniAppDeepClone.icon =
            miniAppDeepClone.icon !== image_default
                ? miniAppDeepClone.icon
                : "";
        miniAppDeepClone.target = targetValue;
        setAllValues(miniAppDeepClone);
        setIsFetching(true);

        let theme, statusToPass, showInputError;

        if (Utils.isValidForRegister(miniAppDeepClone)) {
            tryToRegister(miniAppDeepClone);
        } else {
            theme = "danger";
            showInputError = true;
            statusToPass = "invalid";
            const invalidInputs = Validate.getInvalidInputs(
                miniAppDeepClone,
                false
            );
            viewStateAfterRegister(
                theme,
                showInputError,
                statusToPass,
                invalidInputs
            );
        }
        window.scrollTo(0, 0);
        miniAppDeepClone.icon = miniAppDeepClone.icon
            ? miniAppDeepClone.icon
            : image_default;
    };

    const tryToRegister = async miniApp => {
        let theme, statusToPass, showInputError;
        const EITRI_APP_TYPE = Data.getAppTypes().EITRI_TYPE_ENGINE;
        const miniAppDeepClone = JSON.parse(JSON.stringify(miniApp));
        miniAppDeepClone.type = EITRI_APP_TYPE;
        try {
            const typedMiniapp = buildCreateMiniAppPayload(miniAppDeepClone);
            if (!miniAppDeepClone.privacy.mode) {
                delete typedMiniapp.privacy;
            }
            const res = await MiniAppService.createMiniApp(typedMiniapp);
            theme = "success";
            statusToPass = res.status;
        } catch (error) {
            ErrorHandler.handleError(error);
            const isTruthyResponse = error && !!error.response;
            theme = "danger";
            showInputError = true;

            if (isTruthyResponse) {
                statusToPass = error.response.status;
                if (statusToPass === 401) setHasAuthError(true);
            }
        } finally {
            viewStateAfterRegister(theme, showInputError, statusToPass, []);
        }
    };

    const viewStateAfterRegister = (
        theme,
        showInputError,
        statusToPass,
        invalidInputs
    ) => {
        setIsFetching(false);
        setAlertTheme(theme);
        setShowInputError(showInputError);
        setAlertMessage(
            AlertMessages.messageOnSubmitNewMiniApp(statusToPass, invalidInputs)
        );
        setVisible(true);
    };

    const actions = {
        handleOrganization: event =>
            updateValues(Utils.updateOrganizationName(event, values)),
        handleInput: e => setValue(e),
        handleSwitchStatus: () =>
            updateValues(FormUtils.getNewMiniAppStatus(values)),
        handleSwitchChange: name =>
            updateValues(FormUtils.handleSwitchChange(name, values)),
        handleAddNote: text => updateValues(Utils.updateTags(text, values)),
        closeAlert: () => setVisible(false),
        onLoad: event =>
            updateValues(Utils.loadIcon(event, values, image_default)),
        collapseMoreOptions: () => setCollapse(!collapse),
        onTogglePermission: onTogglePermission,
        onSubmit: registerMiniapp,
        changeTarget: e => setTargetValue(e.target.value)
    };

    return (
        <React.Fragment>
            <FormRegister
                isFetching={isFetching}
                visible={visible}
                alertTheme={alertTheme}
                alertMessage={alertMessage}
                isAdmin={isAdmin}
                hasWriteMiniAppScope={hasWriteMiniAppScope}
                miniApp={values}
                showInputError={showInputError}
                collapse={collapse}
                actions={actions}
                targetValue={targetValue}
            />
        </React.Fragment>
    );
};

export default MiniAppRegisterScreen;
